import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent, 
  CircularProgress, 
  Alert, 
  IconButton, 
  Tooltip,
  Tabs,
  Tab,
  Button
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import AdPlacementControl from '../components/AdPlacementControl';
import AdPlacementMetrics from '../components/AdPlacementMetrics';
import AdPlacementPreview from '../components/AdPlacementPreview';
import AdPlacementCodeGenerator from '../components/AdPlacementCodeGenerator';
import publisherConfigService, { AdPlacementConfig, PublisherConfig } from '../services/publisherConfigService';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

interface PublisherAnalytics {
  impressions: number;
  clicks: number;
  revenue: number;
  ctr: number;
}

// Default configuration
const DEFAULT_CONFIG: AdPlacementConfig = {
  mode: 'after',
  inline_config: {
    tone_matching: true,
    skip_on_factual: true,
    insertion_density: 0.3,
    preferred_positions: ['paragraph_end', 'topic_transition'],
    blending_style: 'subtle'
  },
  response_format: 'auto'
};

const PublisherDashboard: React.FC = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState<PublisherAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [adPlacementConfig, setAdPlacementConfig] = useState<AdPlacementConfig>(DEFAULT_CONFIG);
  const [debugInfo, setDebugInfo] = useState<any>({
    user: null,
    apiUrl: API_URL,
    token: null,
    timestamp: new Date().toISOString(),
    errors: []
  });

  const handleCopyPublisherId = () => {
    if (user?.publisher_id) {
      navigator.clipboard.writeText(user.publisher_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleConfigUpdate = (config: AdPlacementConfig) => {
    setAdPlacementConfig(config);
  };

  // Add a handler to navigate to the Preview tab
  const handleViewPreview = () => {
    setCurrentTab(2); // Set to the Preview tab (index 2)
  };

  // Add a handler to navigate to the Code Generator tab
  const handleViewCode = () => {
    setCurrentTab(3); // Set to the Code Generator tab (index 3)
  };

  const fetchData = async () => {
    try {
      console.log('PublisherDashboard: Starting data fetch');
      console.log('Current user:', user);
      setLoading(true);
      setError(null);
      
      // Update debug info with user data
      setDebugInfo(prev => ({
        ...prev,
        user,
        token: localStorage.getItem('token'),
        timestamp: new Date().toISOString()
      }));
      
      if (!user) {
        console.error('No user data available');
        setError('Please log in to view your dashboard');
        setDebugInfo(prev => ({
          ...prev,
          errors: [...prev.errors, { type: 'auth', message: 'No user data available', timestamp: new Date().toISOString() }]
        }));
        setLoading(false);
        return;
      }
      
      if (!user.publisher_id) {
        console.error('User has no publisher_id');
        setError('Your account is not associated with a publisher');
        setDebugInfo(prev => ({
          ...prev,
          errors: [...prev.errors, { type: 'auth', message: 'User has no publisher_id', timestamp: new Date().toISOString() }]
        }));
        setLoading(false);
        return;
      }
      
      console.log(`Fetching analytics for publisher ID: ${user.publisher_id}`);
      
      // Fetch analytics data
      try {
        const analyticsResponse = await axios.get(`${API_URL}/analytics/publisher/${user.publisher_id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        console.log('Analytics data received:', analyticsResponse.data);
        setAnalytics(analyticsResponse.data);
        setDebugInfo(prev => ({
          ...prev,
          analyticsResponse: analyticsResponse.data
        }));
      } catch (analyticsErr) {
        console.error('Failed to fetch analytics:', analyticsErr);
        setDebugInfo(prev => ({
          ...prev,
          errors: [...prev.errors, { 
            type: 'analytics', 
            message: analyticsErr instanceof Error ? analyticsErr.message : String(analyticsErr),
            timestamp: new Date().toISOString()
          }]
        }));
        // Continue even if analytics fetch fails
      }
      
      // Fetch ad placement config
      console.log(`Fetching config for publisher ID: ${user.publisher_id}`);
      try {
        const configResponse: PublisherConfig = await publisherConfigService.getPublisherConfig(user.publisher_id);
        console.log('Received config:', configResponse);
        
        // Store debug info
        setDebugInfo(prev => ({
          ...prev,
          configResponse,
          timestamp: new Date().toISOString()
        }));
        
        // Check if ad_placement_config exists
        if (configResponse && configResponse.ad_placement_config) {
          console.log('Setting ad placement config:', configResponse.ad_placement_config);
          setAdPlacementConfig(configResponse.ad_placement_config);
        } 
        // Fallback to old schema if ad_placement_config is missing but legacy fields exist
        else if (configResponse && configResponse.ad_placement_mode) {
          console.log('Using old schema format - converting to new format');
          
          // Convert old schema to new schema
          const newConfig: AdPlacementConfig = {
            mode: configResponse.ad_placement_mode as 'before' | 'after' | 'inline',
            response_format: configResponse.response_format_preference as 'text' | 'markdown' | 'html' | 'json' | 'auto' || 'auto'
          };
          
          // Add inline_config if mode is inline
          if (newConfig.mode === 'inline' && configResponse.inline_config) {
            newConfig.inline_config = {
              tone_matching: true,
              skip_on_factual: true,
              insertion_density: 0.3,
              preferred_positions: ['paragraph_end', 'topic_transition'],
              blending_style: 'subtle',
              ...configResponse.inline_config
            };
          }
          
          setAdPlacementConfig(newConfig);
          setDebugInfo(prev => ({
            ...prev,
            convertedConfig: newConfig
          }));
        }
        // Use default configuration if neither format is available
        else {
          console.log('Using default config - no configuration found in response');
          setAdPlacementConfig(DEFAULT_CONFIG);
        }
      } catch (configErr) {
        console.error('Failed to fetch ad placement config, using defaults', configErr);
        if (axios.isAxiosError(configErr)) {
          setDebugInfo(prev => ({
            ...prev,
            configError: {
              message: configErr.message,
              status: configErr.response?.status,
              data: configErr.response?.data
            },
            timestamp: new Date().toISOString(),
            errors: [...prev.errors, { 
              type: 'config', 
              message: configErr.message,
              status: configErr.response?.status,
              data: configErr.response?.data,
              timestamp: new Date().toISOString()
            }]
          }));
        } else {
          setDebugInfo(prev => ({
            ...prev,
            configError: String(configErr),
            timestamp: new Date().toISOString(),
            errors: [...prev.errors, { 
              type: 'config', 
              message: String(configErr),
              timestamp: new Date().toISOString()
            }]
          }));
        }
      }
      } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data');
      setDebugInfo(prev => ({
        ...prev,
        generalError: String(err),
        timestamp: new Date().toISOString(),
        errors: [...prev.errors, { 
          type: 'general', 
          message: err instanceof Error ? err.message : String(err),
          timestamp: new Date().toISOString()
        }]
      }));
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    if (user?.publisher_id) {
      fetchData();
    } else {
      console.log('No publisher_id available, waiting for user data');
      setDebugInfo(prev => ({
        ...prev,
        waitingForUser: true,
        timestamp: new Date().toISOString()
      }));
    }
  }, [user?.publisher_id]);

  // Debug Panel - always visible regardless of errors
  const renderDebugPanel = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>Debug Information</Typography>
      <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
          {JSON.stringify(debugInfo, null, 2)}
        </Typography>
      </Paper>
    </Box>
  );

  // Always show Debug Info tab regardless of loading or error state
  const renderTabs = () => (
    <>
      <Typography variant="h5" gutterBottom sx={{ mt: 4, mb: 2 }}>
        Ad Placement Settings
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange} 
          aria-label="ad placement tabs"
          textColor="inherit"
          sx={{
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-selected': {
                color: '#3B82F6' // Light blue color for selected tab
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#3B82F6'
            }
          }}
        >
          <Tab label="Configuration" />
          <Tab label="Performance" />
          <Tab label="Preview" />
          <Tab label="Code Generator" />
          <Tab label="Debug Info" />
        </Tabs>
      </Box>
      
      <Box hidden={currentTab !== 0}>
        {!error && <AdPlacementControl 
          onConfigUpdate={handleConfigUpdate} 
          config={adPlacementConfig}
          onViewPreview={handleViewPreview}
          onViewCode={handleViewCode}
        />}
      </Box>
      
      <Box hidden={currentTab !== 1}>
        {!error && <AdPlacementMetrics />}
      </Box>
      
      <Box hidden={currentTab !== 2}>
        {!error && <AdPlacementPreview config={adPlacementConfig} />}
      </Box>
      
      <Box hidden={currentTab !== 3}>
        {!error && <AdPlacementCodeGenerator 
          config={adPlacementConfig}
          publisherId={user?.publisher_id || ''}
          publisherName={user?.name || 'Publisher'}
        />}
      </Box>

      <Box hidden={currentTab !== 4}>
        {renderDebugPanel()}
      </Box>
    </>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
        </Box>
        {renderDebugPanel()}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={fetchData}
          sx={{ mb: 3 }}
        >
          Retry
        </Button>
        {renderTabs()}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Publisher Dashboard
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Account Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body1">
              <strong>Name:</strong> {user?.name}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body1">
              <strong>Email:</strong> {user?.email}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1">
                <strong>Publisher ID:</strong> {user?.publisher_id}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                <IconButton 
                  size="small" 
                  onClick={handleCopyPublisherId}
                  sx={{ ml: 1 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Button 
                size="small" 
                startIcon={<RefreshIcon />} 
                onClick={fetchData}
                sx={{ ml: 2 }}
              >
                Refresh Data
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
        Analytics Overview
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Impressions
              </Typography>
              <Typography variant="h4">
                {analytics?.impressions.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Clicks
              </Typography>
              <Typography variant="h4">
                {analytics?.clicks.toLocaleString() || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                CTR
              </Typography>
              <Typography variant="h4">
                {analytics?.ctr ? `${(analytics.ctr * 100).toFixed(2)}%` : '0%'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Revenue
              </Typography>
              <Typography variant="h4">
                ${analytics?.revenue.toFixed(2) || '0.00'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {renderTabs()}
    </Box>
  );
};

export default PublisherDashboard; 